/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Cadence,
  NotificationContentType,
  NotificationCreateInput,
  NotificationType,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';

import AdminAssignmentsService from '../../../api/AdminAssignmentsService';
import AssignmentBuilderFormContainer from '../../layout/AssignmentBuilderFormContainer';
import type { AssignmentFormData } from '@/client/components/admin/assignments/types/assignmentTypes';
import AssignmentNotificationCadenceForm from './AssignmentNotificationCadenceForm';
import AssignmentNotificationDaysBeforeDueForm from './AssignmentNotificationDaysBeforeDueForm';
import AssignmentNotificationEmailMessageForm from './message-forms/AssignmentNotificationEmailMessageForm';
import AssignmentNotificationInAppMessageForm from './message-forms/AssignmentNotificationInAppMessageForm';
import AssignmentNotificationSlackMessageForm from './message-forms/AssignmentNotificationSlackMessageForm';
import AssignmentNotificationTypeMenu from './AssignmentNotificationTypeMenu';
import AssignmentNotificationsList from './AssignmentNotificationsList';
import AssignmentNotificationsMessagingTypeCheckboxGroup from './AssignmentNotificationsMessagingTypeCheckboxGroup';
import { BsPlusCircle } from 'react-icons/bs';
import { Plus } from '@/client/components/icons/ContinuIcons';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { useAdminAssignmentsStore } from '../../../state/adminAssignmentsStore';
import { useAuthStore } from '@/client/services/state/authStore';
import { useMutation } from '@tanstack/react-query';
import { useNotificationBuilderStore } from '@/client/components/admin/assignments/builder/state/AssignmentNotificationBuilderStore';
import { useNotificationEditor } from '@/client/services/hooks/admin/notifications/useNotificationEditor';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export type FullNotificationMessage = NotificationCreateInput & {
  timeBeforeDue?: number;
  cadence: Cadence;
  totalMessages: number;
};

export default function AssignmentNotificationsForm() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { assignmentNotificationTemplateOptions } = useNotificationEditor();
  const { setNotificationsFormVisited, setActiveForm } = useAdminAssignmentsStore();
  const { watch: parentFormWatch, setValue: parentFormSetValue } =
    useFormContext<AssignmentFormData>();
  const {
    notificationBuilderActive,
    setNotificationBuilderActive,
    activeEditNotificationId,
    setActiveEditNotificationId,
  } = useNotificationBuilderStore();
  const [messageFormType, setMessageFormType] = useState<'email' | 'slack' | 'in_app' | null>(null);

  useEffect(() => {
    setActiveForm('notifications');
    setNotificationsFormVisited(true);
  }, []);

  const { settings: parentFormSettings } = parentFormWatch();
  const { type: parentFormType } = parentFormSettings;

  const { reminderMessages, nonReminderMessages, overdueMessages } = parentFormSettings;

  const hasCurrentNotifications =
    reminderMessages.length > 0 || nonReminderMessages.length > 0 || overdueMessages.length > 0;

  // ! Since cadence data is stored on the workflow, we need to combine message and cadence data for the notifications builder
  const rMessages = reminderMessages.map((message) => ({
    ...message.message,
    timeBeforeDue: message.timeBeforeDue,
  }));
  const nrMessages = nonReminderMessages;
  const odMessages = overdueMessages.map((message) => ({
    ...message.message,
    cadence: message.cadence,
    totalMessages: message.totalMessages,
  }));

  const allMessages = [...rMessages, ...nrMessages, ...odMessages];

  const notificationFormDefaultValues: FullNotificationMessage = {
    timeBeforeDue: undefined,
    cadence: Cadence.Daily,
    totalMessages: 1,
    channels: [],
    contentId: null,
    contentType:
      parentFormType === WorkflowType.PointInTime
        ? NotificationContentType.Assignment
        : NotificationContentType.Workflow,
    creator: user._id,
    messaging: {
      email: [
        {
          name: 'from_name',
          value: '',
        },
        {
          name: 'subject',
          value: '',
        },
        {
          name: 'body',
          value: assignmentNotificationTemplateOptions.email.ASSIGNMENT_REMINDER_ASSIGNEE,
        },
      ],
      inApp: [
        {
          name: 'body',
          value: assignmentNotificationTemplateOptions.inApp.ASSIGNMENT_REMINDER_ASSIGNEE,
        },
      ],
      slack: [
        {
          name: 'include_template',
          value: 'active',
        },
        {
          name: 'body',
          value: assignmentNotificationTemplateOptions.slack.ASSIGNMENT_REMINDER_ASSIGNEE,
        },
      ],
    },
    name: '',
    options: [],
    scheduled: false,
    scheduledDuration: 0,
    type: NotificationType.AssignmentCreatedAssignee,
  };

  const methods = useForm<FullNotificationMessage>({
    defaultValues: notificationFormDefaultValues,
    mode: 'onChange',
  });

  const {
    watch,
    setValue,
    control,
    reset,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
  } = methods;

  const {
    channels,
    contentId,
    contentType,
    creator,
    messaging,
    name,
    options,
    scheduled,
    scheduledDuration,
    type,
  } = watch();

  useEffect(() => {
    const channelError = !channels.length;

    if (!channelError) clearErrors('channels');
  }, [channels]);

  useEffect(() => {
    if (activeEditNotificationId) {
      const activeEditNotification = allMessages.find((msg) => msg.id === activeEditNotificationId);

      reset(activeEditNotification);

      return;
    }

    reset(notificationFormDefaultValues);
  }, [activeEditNotificationId]);

  useEffect(() => {
    // Only update templates when not editing an existing notification
    if (!activeEditNotificationId) {
      setValue(
        'messaging.email',
        messaging.email.map((msg) =>
          msg.name === 'body'
            ? { ...msg, value: assignmentNotificationTemplateOptions.email[type] }
            : msg,
        ),
      );

      setValue(
        'messaging.inApp',
        messaging.inApp.map((msg) =>
          msg.name === 'body'
            ? { ...msg, value: assignmentNotificationTemplateOptions.inApp[type] }
            : msg,
        ),
      );

      setValue(
        'messaging.slack',
        messaging.slack.map((msg) =>
          msg.name === 'body'
            ? { ...msg, value: assignmentNotificationTemplateOptions.slack[type] }
            : msg,
        ),
      );
    }
  }, [type, activeEditNotificationId]);

  const nameError = !!errors.name;

  const messageFormTypeMap = {
    email: <AssignmentNotificationEmailMessageForm handleCloseModal={onClose} />,
    slack: <AssignmentNotificationSlackMessageForm handleCloseModal={onClose} />,
    in_app: <AssignmentNotificationInAppMessageForm handleCloseModal={onClose} />,
  };

  const messageFormModalTitleMap = {
    email: t('notifications.messaging.email'),
    slack: t('notifications.messaging.slack'),
    in_app: t('notifications.messaging.inApp'),
  };

  const handleCloseForm = () => {
    reset();
    setActiveEditNotificationId(null);
    setNotificationBuilderActive(false);
  };

  const onUpdateNotification = (notificationId: string) => {
    const data = getValues();
    const notificationType = data.type;

    // * Find existing notification in all arrays
    const existingReminderMessage = reminderMessages.find(
      (msg) => msg.message.id === activeEditNotificationId,
    );
    const existingOverdueMessage = overdueMessages.find(
      (msg) => msg.message.id === activeEditNotificationId,
    );
    const existingNonReminderMessage = nonReminderMessages.find(
      (msg) => msg.id === activeEditNotificationId,
    );

    // * If notification exists, update it in place
    if (existingReminderMessage) {
      const updatedReminderMessages = reminderMessages.map((msg) => {
        if (msg.message.id === activeEditNotificationId) {
          return {
            message: { ...data, id: notificationId, createdAt: '', updatedAt: '' },
            timeBeforeDue: data.timeBeforeDue,
          };
        }
        return msg;
      });
      parentFormSetValue('settings.reminderMessages', updatedReminderMessages);
    } else if (existingOverdueMessage) {
      const updatedOverdueMessages = overdueMessages.map((msg) => {
        if (msg.message.id === activeEditNotificationId) {
          return {
            message: { ...data, id: notificationId, createdAt: '', updatedAt: '' },
            cadence: data.cadence,
            totalMessages: data.totalMessages,
          };
        }
        return msg;
      });
      parentFormSetValue('settings.overdueMessages', updatedOverdueMessages);
    } else if (existingNonReminderMessage) {
      const updatedNonReminderMessages = nonReminderMessages.map((msg) => {
        if (msg.id === activeEditNotificationId) {
          return { ...data, id: notificationId, createdAt: '', updatedAt: '' };
        }
        return msg;
      });
      parentFormSetValue('settings.nonReminderMessages', updatedNonReminderMessages);
    } else {
      // * If notification doesn't exist, add it to appropriate array based on type
      const isReminderNotification =
        notificationType === NotificationType.AssignmentReminderAssignee ||
        notificationType === NotificationType.AssignmentReminderEscalator;

      const isOverdueNotification = notificationType === NotificationType.AssignmentOverdueAssignee;

      if (isReminderNotification) {
        parentFormSetValue('settings.reminderMessages', [
          ...reminderMessages,
          {
            message: { ...data, id: notificationId, createdAt: '', updatedAt: '' },
            timeBeforeDue: data.timeBeforeDue,
          },
        ]);
      } else if (isOverdueNotification) {
        parentFormSetValue('settings.overdueMessages', [
          ...overdueMessages,
          {
            message: { ...data, id: notificationId, createdAt: '', updatedAt: '' },
            cadence: data.cadence,
            totalMessages: data.totalMessages,
          },
        ]);
      } else {
        parentFormSetValue('settings.nonReminderMessages', [
          ...nonReminderMessages,
          { ...data, id: notificationId, createdAt: '', updatedAt: '' },
        ]);
      }
    }

    handleCloseForm();
  };

  const submissionValues = {
    channels,
    contentId,
    contentType,
    creator,
    messaging,
    name,
    options,
    scheduled,
    scheduledDuration,
    type,
  };

  const createOrUpdateNotificationMutation = useMutation({
    mutationFn: () =>
      activeEditNotificationId
        ? AdminAssignmentsService.updateWorkflowNotification(
            activeEditNotificationId,
            submissionValues,
          )
        : AdminAssignmentsService.createWorkflowNotification(submissionValues),
    onSuccess: (successData) => {
      onUpdateNotification(successData.id);

      setToast({
        show: true,
        status: 'success',
        title: t('notifications.save.success'),
      });
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('notifications.save.error'),
      }),
  });

  const { status } = createOrUpdateNotificationMutation;

  const handleSubmit = () => {
    const data = getValues();

    const { channels: notificationChannels } = data;

    const channelsError = !notificationChannels.length;
    const nameErr = !data.name;

    if (nameErr || channelsError) {
      if (nameErr) {
        setError('name', {
          type: 'required',
          message: `${t('admin.assignments.details.name.error')}`,
        });
      }

      if (channelsError) {
        setError('channels', {
          type: 'required',
          message: `${t('admin.assignments.details.channels.error')}`,
        });
      }

      setToast({
        show: true,
        status: 'error',
        title: t('notifications.errors'),
      });

      return;
    }

    createOrUpdateNotificationMutation.mutateAsync();
  };

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="24px" fontWeight={600}>
            {notificationBuilderActive
              ? t('notifications.newNotification')
              : t('notifications.notifications')}
          </Text>

          {!notificationBuilderActive && hasCurrentNotifications && (
            <Button leftIcon={<Plus />} onClick={() => setNotificationBuilderActive(true)}>
              {t('notifications.addNotification')}
            </Button>
          )}
        </Flex>
      }
      formComponent={
        <Box position="relative">
          {notificationBuilderActive ? (
            <FormProvider {...methods}>
              <Box position="relative" paddingBottom={24}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: `${t('admin.assignments.details.name.error')}` }}
                  render={({ field }) => (
                    <FormControl isInvalid={nameError}>
                      <Input
                        {...field}
                        autoFocus
                        variant="ghost"
                        marginY={10}
                        placeholder={`${t('notifications.name.placeholder')}`}
                        size="lg"
                        isRequired
                        wordBreak="break-word"
                        borderBottom="1px solid"
                        borderBottomColor="neutral.300"
                        borderRadius="none"
                        fontSize="20px"
                        fontWeight={600}
                        marginTop={0}
                        paddingInlineStart={0}
                        _placeholder={{
                          color: 'neutral.600',
                        }}
                        _invalid={{
                          borderBottomColor: 'red.500',
                        }}
                      />
                    </FormControl>
                  )}
                />

                <AssignmentNotificationTypeMenu overdueIsDisabled={overdueMessages.length > 0} />

                {(type === NotificationType.AssignmentReminderAssignee ||
                  type === NotificationType.AssignmentReminderEscalator) && (
                  <AssignmentNotificationDaysBeforeDueForm />
                )}

                {type === NotificationType.AssignmentOverdueAssignee && (
                  <AssignmentNotificationCadenceForm />
                )}

                <AssignmentNotificationsMessagingTypeCheckboxGroup
                  onOpen={onOpen}
                  setMessageFormType={setMessageFormType}
                />
              </Box>

              <Modal
                size="5xl"
                isOpen={isOpen && !!messageFormType}
                onClose={onClose}
                trapFocus={false}
              >
                <ModalOverlay />

                <ModalContent
                  borderRadius="16px"
                  maxHeight="80vh"
                  overflowY="auto"
                  paddingTop={8}
                  position="relative"
                >
                  <ModalHeader>
                    {!!messageFormType && messageFormModalTitleMap[messageFormType]}
                  </ModalHeader>

                  <ModalBody padding={0}>
                    {!!messageFormType && messageFormTypeMap[messageFormType]}
                  </ModalBody>
                </ModalContent>
              </Modal>
            </FormProvider>
          ) : hasCurrentNotifications ? (
            <AssignmentNotificationsList />
          ) : (
            <Box padding={6}>
              <VStack marginTop={20} paddingX={24} textAlign="center">
                <Image src="/public/images/noNotifications.svg" alt="No Notifications" />

                <Text fontSize="18px" fontWeight={600}>
                  {t('notifications.createNotification')}
                </Text>

                <Text fontSize="16px" fontWeight={400}>
                  {t('notifications.createNotification.helpText')}
                </Text>
              </VStack>

              <Button
                variant="warmNeutralDashedBorder"
                width="full"
                marginTop={10}
                leftIcon={<BsPlusCircle />}
                onClick={() => setNotificationBuilderActive(true)}
              >
                {t('notifications.newNotification')}
              </Button>
            </Box>
          )}
        </Box>
      }
      bottomAddonComponent={
        notificationBuilderActive && (
          <ButtonGroup
            width="full"
            position="sticky"
            paddingY={4}
            paddingX={6}
            bottom={0}
            gap={2}
            backgroundColor="white"
            boxShadow="0px -4px 12px rgba(0, 0, 0, 0.05)"
          >
            <Button
              variant="cancel"
              onClick={() => {
                reset();
                setActiveEditNotificationId(null);
                setNotificationBuilderActive(false);
              }}
            >
              {t('global.actions.cancel')}
            </Button>

            <SolidButton isLoading={status === 'loading'} onClick={handleSubmit}>
              {t('notifications.saveNotification')}
            </SolidButton>
          </ButtonGroup>
        )
      }
    />
  );
}
